import { Link } from "react-router-dom";
import { Users, Heart, Scale, Shield, FileText, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { RelatedLinks } from "@/components/RelatedLinks";

import familyLawHero from "@/assets/family-law-hero.jpg";

const FamilyLaw = () => {
  const { language } = useLanguage();

  const copy = {
    title: { en: "Family Law", es: "Derecho Familiar", vi: "Luật Gia Đình" },
    subtitle: {
      en: "Compassionate and effective representation in divorce, custody, and all family law matters. Protecting your family's future during difficult times.",
      es: "Representación compasiva y efectiva en divorcio, custodia y todas las materias de derecho familiar. Protegemos el futuro de su familia en momentos difíciles.",
      vi: "Đại diện tận tâm và hiệu quả về ly hôn, quyền nuôi con và mọi vấn đề gia đình. Bảo vệ tương lai gia đình bạn.",
    },
    schedule: { en: "Schedule Consultation", es: "Agende Consulta", vi: "Đặt Lịch Tư Vấn" },
    call: { en: "Call:", es: "Llamar:", vi: "Gọi:" },
    ourServices: { en: "Our Family Law Services", es: "Nuestros Servicios de Derecho Familiar", vi: "Dịch Vụ Luật Gia Đình" },
    ourServicesDesc: {
      en: "Comprehensive legal support for all family law matters",
      es: "Asesoría legal integral para todas las materias de derecho familiar",
      vi: "Hỗ trợ pháp lý toàn diện cho mọi vấn đề gia đình",
    },
    learnMore: { en: "Learn More", es: "Ver Más", vi: "Tìm Hiểu" },
    approachTitle: { en: "Our Approach to Family Law", es: "Nuestro Enfoque en Derecho Familiar", vi: "Cách Tiếp Cận của Chúng Tôi" },
    approach: [
      { title: { en: "Compassionate Guidance", es: "Guía Compasiva", vi: "Hướng Dẫn Tận Tâm" }, desc: { en: "Family law matters are deeply personal and emotional. We provide supportive, understanding legal counsel while protecting your interests.", es: "Los asuntos de familia son personales y emocionales. Brindamos apoyo y asesoría comprensiva, protegiendo sus intereses.", vi: "Các vấn đề gia đình rất nhạy cảm. Chúng tôi tư vấn thấu hiểu và bảo vệ quyền lợi của bạn." } },
      { title: { en: "Child-Focused Solutions", es: "Soluciones Centradas en los Niños", vi: "Giải Pháp Tập Trung Vào Trẻ Em" }, desc: { en: "In custody matters, the child's best interests always come first.", es: "En custodia, el interés superior del menor siempre es primero.", vi: "Trong quyền nuôi con, lợi ích tốt nhất của trẻ luôn đặt lên hàng đầu." } },
      { title: { en: "Strategic Negotiation", es: "Negociación Estratégica", vi: "Đàm Phán Chiến Lược" }, desc: { en: "We seek amicable resolutions when possible, saving you time and stress.", es: "Buscamos acuerdos amistosos cuando es posible, ahorrando tiempo y estrés.", vi: "Chúng tôi hướng đến giải pháp hòa giải khi có thể để tiết kiệm thời gian và căng thẳng." } },
      { title: { en: "Trial-Ready Advocacy", es: "Defensa Lista para Juicio", vi: "Sẵn Sàng Tranh Tụng" }, desc: { en: "When settlement isn't possible, we're experienced trial attorneys ready to present your case.", es: "Cuando no hay acuerdo, somos abogados litigantes con experiencia listos para representarlo.", vi: "Khi không thể dàn xếp, chúng tôi sẵn sàng tranh tụng với kinh nghiệm." } },
    ],
    divorceTitle: { en: "Divorce in Texas: What You Need to Know", es: "Divorcio en Texas: Lo que Debe Saber", vi: "Ly Hôn tại Texas: Điều Cần Biết" },
    divorce: [
      { title: { en: "Community Property State", es: "Estado de Bienes Gananciales", vi: "Chế Độ Tài Sản Chung" }, desc: { en: "Texas is a community property state...", es: "Texas es un estado de bienes gananciales...", vi: "Texas áp dụng chế độ tài sản chung..." } },
      { title: { en: "60-Day Waiting Period", es: "Periodo de Espera de 60 Días", vi: "Thời Gian Chờ 60 Ngày" }, desc: { en: "Texas requires a 60-day waiting period...", es: "Texas requiere un periodo de espera de 60 días...", vi: "Texas yêu cầu thời gian chờ 60 ngày..." } },
      { title: { en: "Child Custody & Support", es: "Custodia y Manutención", vi: "Quyền Nuôi & Cấp Dưỡng" }, desc: { en: "Texas courts prefer joint custody...", es: "Los tribunales de Texas prefieren la custodia compartida...", vi: "Tòa Texas ưu tiên quyền nuôi chung..." } },
    ],
    cpsTitle: { en: "CPS Investigation or Removal?", es: "¿Investigación o Retiro de CPS?", vi: "Điều Tra hoặc Tước Quyền của CPS?" },
    cpsDesc: { en: "If Child Protective Services has contacted you or removed your child, you need immediate legal representation.", es: "Si CPS le ha contactado o ha retirado a su hijo, necesita representación inmediata.", vi: "Nếu CPS liên hệ hoặc tước quyền nuôi con, bạn cần luật sư ngay lập tức." },
    callNow: { en: "Call Now:", es: "Llamar Ahora:", vi: "Gọi Ngay:" },
    whyTitle: { en: "Why Choose Our Family Law Team?", es: "¿Por Qué Elegir Nuestro Equipo de Familia?", vi: "Vì Sao Chọn Đội Ngũ Gia Đình?" },
    why: [
      { title: { en: "Compassionate Support", es: "Apoyo Compasivo", vi: "Hỗ Trợ Tận Tâm" }, desc: { en: "We understand the emotional toll and provide caring guidance.", es: "Entendemos la carga emocional y brindamos guía con empatía.", vi: "Chúng tôi thấu hiểu và hướng dẫn tận tâm." } },
      { title: { en: "Child-Centered Focus", es: "Enfoque en los Niños", vi: "Lấy Trẻ Em Làm Trọng Tâm" }, desc: { en: "Your children's wellbeing is our priority.", es: "El bienestar de sus hijos es nuestra prioridad.", vi: "Phúc lợi của con bạn là ưu tiên của chúng tôi." } },
      { title: { en: "Experienced Advocates", es: "Abogados con Experiencia", vi: "Luật Sư Kinh Nghiệm" }, desc: { en: "Years of experience in negotiation and litigation.", es: "Años de experiencia en negociación y litigio.", vi: "Nhiều năm kinh nghiệm về đàm phán và tranh tụng." } },
    ],
    ctaTitle: { en: "Need Family Law Guidance?", es: "¿Necesita Asesoría en Derecho Familiar?", vi: "Cần Tư Vấn Luật Gia Đình?" },
    ctaDesc: { en: "Whether you're facing divorce, custody issues, or CPS involvement, we're here to protect your family's interests.", es: "Ya sea divorcio, custodia o CPS, protegemos los intereses de su familia.", vi: "Dù là ly hôn, quyền nuôi hay CPS, chúng tôi bảo vệ quyền lợi gia đình bạn." },
  } as const;

  const approachIcons = [Heart, Shield, Scale, FileText];
  const whyIcons = [Heart, Users, Scale];

  const services = [
    { icon: Heart, title: { en: "Divorce", es: "Divorcio", vi: "Ly Hôn" }, desc: { en: "Contested and uncontested divorces, property division and spousal support.", es: "Divorcios contenciosos y de mutuo acuerdo, bienes y manutención.", vi: "Ly hôn tranh chấp/thuận tình, chia tài sản, cấp dưỡng." }, href: "/practice-areas/family-law/divorce" },
    { icon: Users, title: { en: "Child Custody", es: "Custodia de Menores", vi: "Quyền Nuôi Con" }, desc: { en: "Fighting for your parental rights.", es: "Defendemos sus derechos parentales.", vi: "Bảo vệ quyền làm cha mẹ của bạn." }, href: "/practice-areas/family-law/child-custody" },
    { icon: Scale, title: { en: "Child Support", es: "Manutención", vi: "Cấp Dưỡng" }, desc: { en: "Establishing, modifying, and enforcing child support orders.", es: "Establecimiento, modificación y ejecución de manutención.", vi: "Thiết lập, điều chỉnh, thực thi cấp dưỡng." }, href: "/practice-areas/family-law/child-support" },
    { icon: Shield, title: { en: "CPS Defense", es: "Defensa CPS", vi: "Bào Chữa CPS" }, desc: { en: "Defense against CPS investigations.", es: "Defensa ante investigaciones de CPS.", vi: "Bào chữa các vụ việc CPS." }, href: "/practice-areas/family-law/cps-defense" },
    { icon: Heart, title: { en: "Adoption", es: "Adopción", vi: "Nhận Con Nuôi" }, desc: { en: "Stepparent and private adoptions.", es: "Adopciones de padrastros y privadas.", vi: "Nhận con nuôi riêng, cha dượng/mẹ kế." }, href: "/practice-areas/family-law/adoption" },
    { icon: FileText, title: { en: "Court Order Modifications", es: "Modificación de Órdenes", vi: "Sửa Đổi Lệnh Tòa" }, desc: { en: "Modifying custody, visitation, and support orders.", es: "Modificación de custodia, visitas y manutención.", vi: "Sửa đổi lệnh nuôi con, thăm nom, cấp dưỡng." }, href: "/practice-areas/family-law/court-order-modification" },
  ];

  const localizedServices = services.map((s) => ({
    icon: s.icon,
    title: s.title[language],
    description: s.desc[language],
    href: s.href,
  }));

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${familyLawHero})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-primary-foreground">
            <Users className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg">{copy.title[language]}</h1>
            <p className="text-xl opacity-95 mb-8 drop-shadow-md">{copy.subtitle[language]}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="hero">
                <LocalizedLink to="/appointment">{copy.schedule[language]}</LocalizedLink>
              </Button>
              <a href="tel:8329303059">
                <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary-dark bg-white/10 backdrop-blur-sm">
                  {copy.call[language]} (832) 930-3059
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{copy.ourServices[language]}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {copy.ourServicesDesc[language]}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {localizedServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-accent" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="link" className="p-0">
                    <LocalizedLink to={service.href}>
                      {copy.learnMore[language]} <ArrowRight className="ml-1 h-4 w-4" />
                    </LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{copy.approachTitle[language]}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {copy.approach.map((a, i) => {
                const IconComponent = approachIcons[i];
                return (
                  <div key={i} className="bg-card p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                      <IconComponent className="h-6 w-6 text-accent" />
                      {a.title[language]}
                    </h3>
                    <p className="text-muted-foreground">{a.desc[language]}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Divorce Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{copy.divorceTitle[language]}</h2>
            <div className="space-y-6">
              {copy.divorce.map((d, i) => (
                <div key={i} className="bg-card p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-bold mb-3">{d.title[language]}</h3>
                  <p className="text-muted-foreground">{d.desc[language]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CPS Defense Section */}
      <section className="py-20 bg-destructive/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="h-16 w-16 mx-auto mb-6 text-destructive" />
            <h2 className="text-4xl font-bold mb-4">{copy.cpsTitle[language]}</h2>
            <p className="text-xl text-muted-foreground mb-8">{copy.cpsDesc[language]}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059">
                <Button size="lg" variant="default">
                  {copy.callNow[language]} (832) 930-3059
                </Button>
              </a>
              <Button asChild size="lg" variant="outline">
                <LocalizedLink to="/practice-areas/family-law/cps-defense">
                  {copy.learnMore[language]}
                </LocalizedLink>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">{copy.whyTitle[language]}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {copy.why.map((w, i) => {
                const IconComponent = whyIcons[i];
                return (
                  <div key={i}>
                    <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{w.title[language]}</h3>
                    <p className="text-sm text-muted-foreground">{w.desc[language]}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <RelatedLinks category="family" />

      <CTASection
        title={copy.ctaTitle[language]}
        description={copy.ctaDesc[language]}
      />

      <Footer />
    </div>
  );
};

export default FamilyLaw;
